import { useState } from "react";
import { track } from "../../../analytics";
import { userApi } from "../../../lib/api";
import { useSession } from "../../../lib/session";
import { useFapsStore } from "../../../lib/faps";
import { useCrownBursts } from "../../../lib/useCrownBursts";
import { AuthModal } from "../AuthModal";
import { CrownMark } from "../../ui/Crown";
import { cx } from "../../ui/primitives";

/**
 * The crown on a post — CollectionDetail's per-tile pill, generalised (2026-09-13). It
 * IS the crown on the post's clip: the same `faps` row, the same pay, the same count
 * everywhere the clip appears. Spammable like FapBar (every tap saves and bursts, never
 * un-crowns); the count moves by one on the FIRST tap this mount and then reconciles to
 * the server's number, so mashing does not inflate it. Crown + number, never the word,
 * never money (faps-copy-rules).
 */
export function CrownPill({ postId, crowns, onCrowns }: { postId: string; crowns: number; onCrowns: (n: number) => void }) {
  const user = useSession((s) => s.user);
  const [authOpen, setAuthOpen] = useState(false);
  const [crowned, setCrowned] = useState(false);
  const { bursts, pushBurst } = useCrownBursts();

  const crown = () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    if (!crowned) {
      setCrowned(true);
      onCrowns(crowns + 1);
    }
    pushBurst();
    track("fap_saved", { surface: "feed" });
    userApi
      .feedCrown(postId)
      .then((r) => {
        onCrowns(r.crowns);
        // The whole reply, paid or not: the store reads `capped` off a tap that paid nothing.
        useFapsStore.getState().applyClick(r);
      })
      .catch(() => undefined);
  };

  return (
    <>
      <span className="relative inline-flex">
        <button
          type="button"
          onClick={crown}
          aria-pressed={crowned}
          title={!user ? "Sign in to crown this" : "Crown it — saves the clip to your Faps"}
          className={cx(
            "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold text-white transition-colors",
            crowned ? "bg-[#f5c518]/25" : "bg-[var(--color-panel-2)] hover:bg-white/10",
          )}
        >
          <CrownMark size={16} tone={crowned ? "gold" : "muted"} />
          <span className="leading-none">{crowns}</span>
        </button>
        <span className="pointer-events-none absolute inset-0">
          {bursts.map((id) => (
            <span key={id} className="absolute left-1 top-0 animate-[fapFloat_0.9s_ease-out_forwards]">
              <CrownMark size={18} className="drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]" />
            </span>
          ))}
        </span>
      </span>
      {authOpen && (
        <AuthModal initialMode="register" reason="Create a free account to crown posts." onClose={() => setAuthOpen(false)} />
      )}
    </>
  );
}
